import React, { useState, useEffect } from 'react';
import { apiFetch } from '../lib/api';
import { Card, Btn, SLabel } from '../components/ui';
import { useAuth } from '../hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import { subscribeToPush, unsubscribeFromPush, isPushSubscribed, isPushSupported } from '../lib/pushNotifications';
import { biometricEnabled, setBiometricEnabled, biometricAvailable, biometricKindLabel, authenticateBiometric } from '../lib/biometricAuth';
import { Capacitor } from '@capacitor/core';
import useIsMobile from '../hooks/useIsMobile';

function SettingsRow({ icon, iconColor, label, value, onClick, chevron = true, border = true }) {
  return (
    <button onClick={onClick}
      style={{ display:'flex', alignItems:'center', gap:14, padding:'14px 20px', width:'100%', background:'none', border:'none',
        borderBottom: border ? '1px solid var(--border)' : 'none', cursor:'pointer', fontFamily:'inherit', textAlign:'left' }}>
      <div style={{ width:36, height:36, borderRadius:10, background:`${iconColor || 'var(--blue)'}18`, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
        <i className={icon} style={{ fontSize:14, color: iconColor || 'var(--blue)' }}></i>
      </div>
      <span style={{ flex:1, fontSize:15, fontWeight:600, color:'var(--text)' }}>{label}</span>
      {value && <span style={{ fontSize:13, color:'var(--text3)', marginRight:4 }}>{value}</span>}
      {chevron && <i className="fa-solid fa-chevron-right" style={{ fontSize:11, color:'var(--text3)' }}></i>}
    </button>
  );
}

export default function SettingsPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  const [section, setSection] = useState(null); // null = main list, 'profile' | 'password' | 'appearance'

  const [firstName, setFirstName] = useState(user?.firstName || '');
  const [lastName,  setLastName]  = useState(user?.lastName  || '');
  const [saving,    setSaving]    = useState(false);
  const [saveMsg,   setSaveMsg]   = useState('');

  const [curPassword, setCurPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPw,   setConfirmPw]   = useState('');
  const [pwSaving,    setPwSaving]    = useState(false);
  const [pwMsg,       setPwMsg]       = useState('');
  const [pwError,     setPwError]     = useState('');

  const [theme, setTheme] = useState(() => document.documentElement.getAttribute('data-theme') || 'light');
  const [bioEnabled, setBioEnabled] = useState(biometricEnabled());
  const [bioAvail, setBioAvail] = useState(false);
  const [bioLabel, setBioLabel] = useState('Face ID');
  const [bioError, setBioError] = useState('');
  const IS_NATIVE = Capacitor.isNativePlatform();
  useEffect(() => {
    if (!IS_NATIVE) return;
    biometricAvailable().then(setBioAvail);
    biometricKindLabel().then(setBioLabel);
  }, []);
  const toggleBiometric = async () => {
    setBioError('');
    if (!bioEnabled) {
      const ok = await authenticateBiometric(`Enable ${bioLabel} for Day Sheets`);
      if (ok) { setBiometricEnabled(true); setBioEnabled(true); }
      else setBioError(`${bioLabel} check failed. Try again.`);
    } else {
      setBiometricEnabled(false);
      setBioEnabled(false);
    }
  };
  const [admins, setAdmins] = useState([]);
  const [transferTarget, setTransferTarget] = useState('');
  const [transferring, setTransferring] = useState(false);
  const [pushSupported] = useState(() => isPushSupported());
  const [pushEnabled, setPushEnabled] = useState(false);
  const [pushToggling, setPushToggling] = useState(false);
  const [pushArtists, setPushArtists] = useState([]);
  const [pushTitle, setPushTitle] = useState('');
  const [pushBody, setPushBody] = useState('');
  const [pushArtistId, setPushArtistId] = useState('');
  const [pushSending, setPushSending] = useState(false);
  const [pushMsg, setPushMsg] = useState('');
  const [accessCode, setAccessCode] = useState('');
  const [codeLoading, setCodeLoading] = useState(false);
  const [codeMsg, setCodeMsg] = useState(null);

  useEffect(() => {
    if (user?.isFirstUser) {
      apiFetch('/users').then(users => {
        setAdmins((users || []).filter(u => u.role === 'admin' && u.id !== user.id));
      }).catch(() => {});
    }
    isPushSubscribed().then(setPushEnabled).catch(() => {});
    if (user?.role === 'admin') {
      apiFetch('/artists').then(res => setPushArtists((res.artists || res || []))).catch(() => {});
    }
  }, [user]);

  const inp = { width:'100%', background:'var(--bg)', border:'1px solid var(--border)', borderRadius:10, padding:'11px 14px', fontSize:14, color:'var(--text)', outline:'none', boxSizing:'border-box' };

  const handleSaveProfile = async () => {
    setSaving(true); setSaveMsg('');
    try {
      await apiFetch('/users/' + user.id, { method:'PATCH', body: JSON.stringify({ firstName, lastName }) });
      setSaveMsg('Profile updated!');
      setTimeout(() => setSaveMsg(''), 3000);
    } catch(e) { setSaveMsg('Error: ' + e.message); }
    finally { setSaving(false); }
  };

  const handleChangePassword = async () => {
    setPwError(''); setPwMsg('');
    if (newPassword.length < 6) { setPwError('Password must be at least 6 characters'); return; }
    if (newPassword !== confirmPw) { setPwError('Passwords do not match'); return; }
    setPwSaving(true);
    try {
      await apiFetch('/users/' + user.id + '/change-password', { method:'POST', body: JSON.stringify({ currentPassword: curPassword, newPassword }) });
      setPwMsg('Password updated!');
      setCurPassword(''); setNewPassword(''); setConfirmPw('');
      setTimeout(() => setPwMsg(''), 3000);
    } catch(e) { setPwError(e.message); }
    finally { setPwSaving(false); }
  };

  const handleLogout = () => { logout(); navigate('/login'); };

  const applyTheme = (t) => {
    const val = t === 'dark' ? '' : 'light';
    document.documentElement.setAttribute('data-theme', val);
    localStorage.setItem('ds_theme', val);
    setTheme(t);
  };

  const BackBtn = () => (
    <button onClick={() => setSection(null)}
      style={{ display:'inline-flex', alignItems:'center', gap:4, background:'none', border:'none', cursor:'pointer', color:'var(--blue)', fontSize:14, fontWeight:600, fontFamily:'inherit', padding:'0 0 16px', marginBottom:4 }}>
      <i className="fa-solid fa-chevron-left" style={{ fontSize:10 }}></i> Settings
    </button>
  );

  // ── Sub-sections ──
  if (section === 'profile') {
    return (
      <div style={{ padding: isMobile ? '20px 16px' : '32px 40px', maxWidth:640, margin:'0 auto' }}>
        <BackBtn />
        <h2 style={{ fontSize:22, fontWeight:900, color:'var(--text)', marginBottom:24 }}>Edit Profile</h2>
        <Card style={{ padding:24 }}>
          <div style={{ display:'flex', alignItems:'center', gap:16, marginBottom:20 }}>
            <div style={{ width:60, height:60, borderRadius:'50%', background:'rgba(79,156,249,0.15)', border:'2px solid rgba(79,156,249,0.3)',
              display:'flex', alignItems:'center', justifyContent:'center', fontSize:22, fontWeight:800, color:'var(--blue)', overflow:'hidden', flexShrink:0 }}>
              {user?.photo ? <img src={user.photo} alt="" style={{ width:'100%', height:'100%', objectFit:'cover' }} /> : (user?.firstName?.[0]||'?').toUpperCase()}
            </div>
            <div>
              <p style={{ fontSize:13, color:'var(--text3)' }}>{user?.email}</p>
              <p style={{ fontSize:12, color:'var(--blue)', textTransform:'capitalize', marginTop:2 }}>{user?.role}</p>
            </div>
          </div>
          <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
            <div style={{ display:'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap:12 }}>
              <div><SLabel>First Name</SLabel><input value={firstName} onChange={e=>setFirstName(e.target.value)} style={inp} /></div>
              <div><SLabel>Last Name</SLabel><input value={lastName} onChange={e=>setLastName(e.target.value)} style={inp} /></div>
            </div>
            <div style={{ display:'flex', alignItems:'center', gap:12 }}>
              <Btn variant="primary" onClick={handleSaveProfile} disabled={saving}>{saving?'Saving...':'Save Profile'}</Btn>
              {saveMsg && <p style={{ fontSize:13, color:'#3fb950' }}>{saveMsg}</p>}
            </div>
          </div>
        </Card>
      </div>
    );
  }

  if (section === 'password') {
    return (
      <div style={{ padding: isMobile ? '20px 16px' : '32px 40px', maxWidth:640, margin:'0 auto' }}>
        <BackBtn />
        <h2 style={{ fontSize:22, fontWeight:900, color:'var(--text)', marginBottom:24 }}>Change Password</h2>
        <Card style={{ padding:24 }}>
          <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
            <div><SLabel>Current Password</SLabel><input type="password" value={curPassword} onChange={e=>setCurPassword(e.target.value)} style={inp} /></div>
            <div><SLabel>New Password</SLabel><input type="password" value={newPassword} onChange={e=>setNewPassword(e.target.value)} placeholder="Min 6 characters" style={inp} /></div>
            <div><SLabel>Confirm New Password</SLabel><input type="password" value={confirmPw} onChange={e=>setConfirmPw(e.target.value)} style={inp} /></div>
            {pwError && <p style={{ fontSize:13, color:'var(--red)' }}>{pwError}</p>}
            {pwMsg && <p style={{ fontSize:13, color:'#3fb950' }}>{pwMsg}</p>}
            <Btn variant="primary" onClick={handleChangePassword} disabled={pwSaving}>{pwSaving?'Updating...':'Update Password'}</Btn>
          </div>
        </Card>
      </div>
    );
  }

  if (section === 'appearance') {
    return (
      <div style={{ padding: isMobile ? '20px 16px' : '32px 40px', maxWidth:640, margin:'0 auto' }}>
        <BackBtn />
        <h2 style={{ fontSize:22, fontWeight:900, color:'var(--text)', marginBottom:24 }}>Appearance</h2>
        <Card style={{ padding:0, overflow:'hidden' }}>
          {['dark', 'light'].map((t, i) => (
            <button key={t} onClick={() => applyTheme(t)}
              style={{ display:'flex', alignItems:'center', gap:14, padding:'16px 20px', width:'100%', background:'none', border:'none',
                borderBottom: i === 0 ? '1px solid var(--border)' : 'none', cursor:'pointer', fontFamily:'inherit', textAlign:'left' }}>
              <div style={{ width:40, height:40, borderRadius:12, overflow:'hidden', border: theme === t ? '2px solid var(--blue)' : '2px solid var(--border)', flexShrink:0, display:'flex' }}>
                <div style={{ flex:1, background: t === 'dark' ? '#0F172A' : '#ffffff' }} />
                <div style={{ flex:1, background: t === 'dark' ? '#1E293B' : '#f0f0f0' }} />
              </div>
              <div style={{ flex:1 }}>
                <p style={{ fontSize:15, fontWeight:700, color:'var(--text)', margin:0, textTransform:'capitalize' }}>{t}</p>
                <p style={{ fontSize:12, color:'var(--text3)', margin:'2px 0 0' }}>{t === 'dark' ? 'Easy on the eyes' : 'Classic light mode'}</p>
              </div>
              {theme === t && <i className="fa-solid fa-check-circle" style={{ fontSize:18, color:'var(--blue)' }}></i>}
            </button>
          ))}
        </Card>
      </div>
    );
  }

  if (section === 'notifications') {
    const isAdmin = user?.role === 'admin';
    const handleTogglePush = async () => {
      setPushToggling(true);
      try {
        if (pushEnabled) { await unsubscribeFromPush(); setPushEnabled(false); }
        else { await subscribeToPush(); setPushEnabled(true); }
      } catch (e) { alert(e.message); }
      finally { setPushToggling(false); }
    };
    const handleTestPush = async () => {
      try {
        await apiFetch('/push/test', { method: 'POST' });
        alert('Test notification sent!');
      } catch (e) { alert('Error: ' + e.message); }
    };
    const handleSendPush = async () => {
      if (!pushTitle.trim()) return;
      setPushSending(true); setPushMsg('');
      try {
        const res = await apiFetch('/push/send', { method: 'POST', body: JSON.stringify({ title: pushTitle.trim(), body: pushBody.trim(), artistId: pushArtistId || undefined }) });
        setPushMsg(`Sent to ${res.sent} device${res.sent !== 1 ? 's' : ''}`);
        setPushTitle(''); setPushBody(''); setPushArtistId('');
        setTimeout(() => setPushMsg(''), 4000);
      } catch (e) { setPushMsg('Error: ' + e.message); }
      finally { setPushSending(false); }
    };

    return (
      <div style={{ padding: isMobile ? '20px 16px' : '32px 40px', maxWidth:640, margin:'0 auto' }}>
        <BackBtn />
        <h2 style={{ fontSize:22, fontWeight:900, color:'var(--text)', marginBottom:24 }}>Notifications</h2>

        {/* Push toggle */}
        <Card style={{ padding:20, marginBottom:20 }}>
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between' }}>
            <div>
              <p style={{ fontSize:15, fontWeight:700, color:'var(--text)', margin:0 }}>Push Notifications</p>
              <p style={{ fontSize:12, color:'var(--text3)', margin:'4px 0 0' }}>
                {!pushSupported ? 'Not supported on this device' : pushEnabled ? 'Enabled on this device' : 'Disabled'}
              </p>
            </div>
            <button onClick={handleTogglePush} disabled={!pushSupported || pushToggling}
              style={{ padding:'8px 18px', borderRadius:20, fontFamily:'inherit', fontSize:13, fontWeight:700, cursor: pushSupported ? 'pointer' : 'default', border:'none',
                background: pushEnabled ? '#22c55e' : 'var(--bg)', color: pushEnabled ? '#fff' : 'var(--text3)',
                opacity: (!pushSupported || pushToggling) ? 0.4 : 1, transition:'all 0.15s' }}>
              {pushToggling ? '...' : pushEnabled ? 'On' : 'Off'}
            </button>
          </div>
          {pushEnabled && isAdmin && (
            <button onClick={handleTestPush}
              style={{ marginTop:12, padding:'8px 14px', borderRadius:8, background:'var(--bg)', border:'1px solid var(--border)', color:'var(--text2)', fontSize:12, fontWeight:600, cursor:'pointer', fontFamily:'inherit' }}>
              <i className="fa-solid fa-bell" style={{ marginRight:6, fontSize:10 }}></i>Send Test
            </button>
          )}
        </Card>

        {/* Admin broadcast */}
        {isAdmin && (
          <Card style={{ padding:20 }}>
            <p style={{ fontSize:10, fontWeight:700, letterSpacing:1.5, color:'var(--text3)', margin:'0 0 12px', textTransform:'uppercase' }}>
              <i className="fa-solid fa-bullhorn" style={{ marginRight:4, fontSize:9 }}></i> Send Push Notification
            </p>
            <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
              <div>
                <SLabel>Artist (optional)</SLabel>
                <select value={pushArtistId} onChange={e => setPushArtistId(e.target.value)}
                  style={{ ...inp, appearance:'auto' }}>
                  <option value="">All users (broadcast)</option>
                  {pushArtists.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                </select>
              </div>
              <div>
                <SLabel>Title</SLabel>
                <input value={pushTitle} onChange={e => setPushTitle(e.target.value)} placeholder="Schedule Update" style={inp} />
              </div>
              <div>
                <SLabel>Message</SLabel>
                <textarea value={pushBody} onChange={e => setPushBody(e.target.value)} placeholder="Details..." rows={2} style={{ ...inp, resize:'vertical', fontFamily:'inherit' }} />
              </div>
              {pushMsg && <p style={{ fontSize:13, color: pushMsg.startsWith('Error') ? '#ef4444' : '#22c55e', fontWeight:600 }}>{pushMsg}</p>}
              <Btn variant="primary" onClick={handleSendPush} disabled={pushSending || !pushTitle.trim()}>
                {pushSending ? 'Sending...' : 'Send Notification'}
              </Btn>
            </div>
          </Card>
        )}
      </div>
    );
  }

  if (section === 'transfer') {
    const handleTransfer = async () => {
      if (!transferTarget) return;
      const target = admins.find(a => a.id === transferTarget);
      if (!target) return;
      if (!window.confirm(`Transfer ownership to ${target.firstName || target.email}? You will lose owner privileges.`)) return;
      setTransferring(true);
      try {
        await apiFetch('/users/transfer-owner', { method: 'POST', body: JSON.stringify({ targetUserId: transferTarget }) });
        alert('Ownership transferred. Please log in again.');
        logout();
        navigate('/login');
      } catch (e) { alert('Error: ' + e.message); }
      finally { setTransferring(false); }
    };

    return (
      <div style={{ padding: isMobile ? '20px 16px' : '32px 40px', maxWidth:640, margin:'0 auto' }}>
        <BackBtn />
        <h2 style={{ fontSize:22, fontWeight:900, color:'var(--text)', marginBottom:8 }}>Transfer Ownership</h2>
        <p style={{ fontSize:13, color:'var(--text3)', marginBottom:24, lineHeight:1.5 }}>
          Transfer the owner role to another admin. They will get full access to all artists and settings. You will remain an admin but lose owner privileges.
        </p>
        <Card style={{ padding:24 }}>
          {admins.length === 0 ? (
            <p style={{ fontSize:14, color:'var(--text3)', textAlign:'center', padding:20 }}>
              No other admins to transfer to. Promote someone to admin first.
            </p>
          ) : (
            <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
              <SLabel>Select new owner</SLabel>
              <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
                {admins.map(a => {
                  const name = [a.firstName, a.lastName].filter(Boolean).join(' ') || a.email;
                  const selected = transferTarget === a.id;
                  return (
                    <button key={a.id} onClick={() => setTransferTarget(a.id)}
                      style={{ display:'flex', alignItems:'center', gap:12, padding:'12px 16px', borderRadius:12, cursor:'pointer', fontFamily:'inherit', textAlign:'left',
                        background: selected ? 'rgba(251,191,36,0.08)' : 'var(--bg)',
                        border: `2px solid ${selected ? '#fbbf24' : 'var(--border)'}`,
                        transition:'all 0.15s' }}>
                      <div style={{ width:36, height:36, borderRadius:'50%', background:'rgba(251,191,36,0.12)', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0, overflow:'hidden' }}>
                        {a.photo ? <img src={a.photo} alt="" style={{ width:'100%', height:'100%', objectFit:'cover' }} /> :
                          <span style={{ fontSize:14, fontWeight:800, color:'#fbbf24' }}>{(a.firstName?.[0] || a.email[0]).toUpperCase()}</span>}
                      </div>
                      <div style={{ flex:1 }}>
                        <p style={{ fontSize:14, fontWeight:700, color:'var(--text)', margin:0 }}>{name}</p>
                        <p style={{ fontSize:12, color:'var(--text3)', margin:'2px 0 0' }}>{a.email}</p>
                      </div>
                      {selected && <i className="fa-solid fa-crown" style={{ fontSize:14, color:'#fbbf24' }}></i>}
                    </button>
                  );
                })}
              </div>
              <Btn variant="primary" onClick={handleTransfer} disabled={!transferTarget || transferring}
                style={{ marginTop:8, background: transferTarget ? 'linear-gradient(135deg,#f59e0b,#d97706)' : undefined }}>
                {transferring ? 'Transferring...' : 'Transfer Ownership'}
              </Btn>
            </div>
          )}
        </Card>
      </div>
    );
  }

  if (section === 'access-code') {
    const handleRedeem = async () => {
      if (!accessCode.trim()) return;
      setCodeLoading(true); setCodeMsg(null);
      try {
        const result = await apiFetch('/auth/redeem-code', { method: 'POST', body: JSON.stringify({ code: accessCode.trim() }) });
        setCodeMsg({ type: 'success', text: `Joined ${result.artist?.name || 'team'}!` });
        setAccessCode('');
      } catch (e) {
        setCodeMsg({ type: 'error', text: e.message || 'Invalid code' });
      } finally { setCodeLoading(false); }
    };
    return (
      <div style={{ padding: isMobile ? '20px 16px' : '32px 40px', maxWidth:640, margin:'0 auto' }}>
        <BackBtn />
        <h2 style={{ fontSize:22, fontWeight:900, color:'var(--text)', marginBottom:8 }}>Enter Access Code</h2>
        <p style={{ fontSize:13, color:'var(--text3)', marginBottom:24, lineHeight:1.5 }}>
          Enter a code from a tour manager or admin to join their artist's team.
        </p>
        <Card style={{ padding:24 }}>
          <SLabel>Access Code</SLabel>
          <input
            value={accessCode}
            onChange={e => setAccessCode(e.target.value.toUpperCase())}
            placeholder="ENTER CODE"
            maxLength={20}
            autoFocus
            onKeyDown={e => e.key === 'Enter' && handleRedeem()}
            style={{
              width:'100%', padding:'14px 18px', borderRadius:12, background:'var(--bg)', border:'2px solid var(--border)',
              color:'var(--text)', fontFamily:'inherit', fontSize:18, fontWeight:700, textAlign:'center',
              letterSpacing:3, textTransform:'uppercase', outline:'none', boxSizing:'border-box', marginTop:8, marginBottom:16
            }}
            onFocus={e => e.target.style.borderColor = '#1E40AF'}
            onBlur={e => e.target.style.borderColor = 'var(--border)'}
          />
          {codeMsg && (
            <div style={{ padding:'10px 14px', borderRadius:10, marginBottom:16,
              background: codeMsg.type === 'success' ? 'rgba(34,197,94,0.08)' : 'rgba(239,68,68,0.08)',
              border: `1px solid ${codeMsg.type === 'success' ? 'rgba(34,197,94,0.3)' : 'rgba(239,68,68,0.3)'}`,
              color: codeMsg.type === 'success' ? '#22c55e' : '#ef4444', fontSize:13, fontWeight:600 }}>
              <i className={`fa-solid ${codeMsg.type === 'success' ? 'fa-check-circle' : 'fa-exclamation-circle'}`} style={{ marginRight:6 }}></i>
              {codeMsg.text}
            </div>
          )}
          <Btn variant="primary" onClick={handleRedeem} disabled={codeLoading || !accessCode.trim()} style={{ width:'100%' }}>
            {codeLoading ? <><i className="fa-solid fa-spinner fa-spin" style={{ marginRight:6 }}></i> Joining...</> : <><i className="fa-solid fa-arrow-right" style={{ marginRight:6 }}></i> Join Team</>}
          </Btn>
        </Card>
      </div>
    );
  }

  // ── Main settings list ──
  return (
    <div style={{ padding: isMobile ? '20px 16px' : '32px 40px', maxWidth:640, margin:'0 auto' }}>
      <h1 style={{ fontSize:28, fontWeight:900, color:'var(--text)', marginBottom:24 }}>Settings</h1>

      {/* Profile card */}
      <button onClick={() => setSection('profile')}
        style={{ display:'flex', alignItems:'center', gap:16, width:'100%', background:'var(--card)', border:'1px solid var(--border)', borderRadius:16, padding:20, cursor:'pointer', fontFamily:'inherit', textAlign:'left', marginBottom:20 }}>
        <div style={{ width:52, height:52, borderRadius:'50%', background:'rgba(79,156,249,0.15)', border:'2px solid rgba(79,156,249,0.3)',
          display:'flex', alignItems:'center', justifyContent:'center', fontSize:20, fontWeight:800, color:'var(--blue)', overflow:'hidden', flexShrink:0 }}>
          {user?.photo ? <img src={user.photo} alt="" style={{ width:'100%', height:'100%', objectFit:'cover' }} /> : (user?.firstName?.[0]||'?').toUpperCase()}
        </div>
        <div style={{ flex:1 }}>
          <p style={{ fontSize:16, fontWeight:700, color:'var(--text)', margin:0 }}>{user?.firstName} {user?.lastName}</p>
          <p style={{ fontSize:13, color:'var(--text3)', margin:'2px 0 0' }}>{user?.email}</p>
        </div>
        <i className="fa-solid fa-chevron-right" style={{ fontSize:12, color:'var(--text3)' }}></i>
      </button>

      {/* Navigation */}
      {isMobile && (
        <Card style={{ padding:0, marginBottom:20, overflow:'hidden' }}>
          <div style={{ padding:'12px 20px 8px' }}>
            <p style={{ fontSize:10, fontWeight:700, letterSpacing:1.5, color:'var(--text3)', margin:0, textTransform:'uppercase' }}>Pages</p>
          </div>
          <SettingsRow icon="fa-solid fa-users"       iconColor="#6366f1" label="Users"              onClick={() => navigate('/admin/people')} />
          <SettingsRow icon="fa-solid fa-envelope"    iconColor="#ec4899" label="Email Distribution" onClick={() => navigate('/admin/distribution')} />
          <SettingsRow icon="fa-regular fa-calendar"  iconColor="#1E40AF" label="Events"              onClick={() => navigate('/admin/events')} />
          <SettingsRow icon="fa-solid fa-plane"       iconColor="#06B6D4" label="Travel"              onClick={() => navigate('/admin/travel')} />
          <SettingsRow icon="fa-solid fa-eye"         iconColor="#a855f7" label="Viewer"              onClick={() => navigate('/viewer')} border={false} />
        </Card>
      )}

      {/* Preferences */}
      <Card style={{ padding:0, marginBottom:20, overflow:'hidden' }}>
        <div style={{ padding:'12px 20px 8px' }}>
          <p style={{ fontSize:10, fontWeight:700, letterSpacing:1.5, color:'var(--text3)', margin:0, textTransform:'uppercase' }}>Preferences</p>
        </div>
        <SettingsRow icon="fa-solid fa-circle-half-stroke" iconColor="#f59e0b" label="Appearance" value={theme === 'dark' ? 'Dark' : 'Light'} onClick={() => setSection('appearance')} />
        <SettingsRow icon="fa-solid fa-bell" iconColor="#22c55e" label="Notifications" value={pushEnabled ? 'On' : 'Off'} onClick={() => setSection('notifications')} />
        {IS_NATIVE && bioAvail && (
          <div onClick={toggleBiometric}
            style={{ display:'flex', alignItems:'center', padding:'14px 20px', borderBottom:'1px solid var(--border)', cursor:'pointer' }}>
            <div style={{ width:30, height:30, borderRadius:8, background:'#4f46e5', display:'flex', alignItems:'center', justifyContent:'center', marginRight:14 }}>
              <i className="fa-solid fa-face-smile" style={{ fontSize:14, color:'#fff' }}></i>
            </div>
            <div style={{ flex:1 }}>
              <p style={{ fontSize:15, fontWeight:700, color:'var(--text)', margin:0 }}>{`Require ${bioLabel}`}</p>
              {bioError && <p style={{ fontSize:11, color:'var(--red)', margin:'2px 0 0' }}>{bioError}</p>}
            </div>
            <span style={{ padding:'6px 14px', borderRadius:14, fontSize:12, fontWeight:700,
              background: bioEnabled ? '#22c55e' : 'var(--bg)', color: bioEnabled ? '#fff' : 'var(--text3)' }}>
              {bioEnabled ? 'On' : 'Off'}
            </span>
          </div>
        )}
        <SettingsRow icon="fa-solid fa-lock" iconColor="#6366f1" label="Change Password" onClick={() => setSection('password')} />
        <SettingsRow icon="fa-solid fa-key" iconColor="#f59e0b" label="Enter Access Code" onClick={() => { setSection('access-code'); setAccessCode(''); setCodeMsg(null); }} border={false} />
      </Card>

      {/* Account */}
      <Card style={{ padding:0, overflow:'hidden' }}>
        <div style={{ padding:'12px 20px 8px' }}>
          <p style={{ fontSize:10, fontWeight:700, letterSpacing:1.5, color:'var(--text3)', margin:0, textTransform:'uppercase' }}>Account</p>
        </div>
        {user?.isFirstUser && (
          <SettingsRow icon="fa-solid fa-crown" iconColor="#fbbf24" label="Transfer Ownership" onClick={() => setSection('transfer')} border={true} />
        )}
        <SettingsRow icon="fa-solid fa-arrow-right-from-bracket" iconColor="#ef4444" label="Sign Out" chevron={false} border={false} onClick={handleLogout} />
      </Card>
    </div>
  );
}
